import { forwardRef, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { leftIcon, rightIcon, invalid, className, ...props },
  ref
) {
  return (
    <div className="relative">
      {leftIcon && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">
          {leftIcon}
        </span>
      )}
      <input
        ref={ref}
        className={cn(
          "w-full rounded-lg border bg-zinc-900/50 text-sm text-zinc-100 placeholder:text-zinc-500",
          "h-9 transition-colors",
          "focus:bg-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30",
          leftIcon  ? "pl-9"  : "pl-3",
          rightIcon ? "pr-9"  : "pr-3",
          invalid ? "border-rose-500/60 focus:border-rose-400" : "border-zinc-800 focus:border-indigo-500/50",
          className
        )}
        {...props}
      />
      {rightIcon && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">
          {rightIcon}
        </span>
      )}
    </div>
  );
});

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, ...props },
  ref
) {
  return (
    <textarea
      ref={ref}
      className={cn(
        "w-full rounded-lg border bg-zinc-900/50 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500",
        "transition-colors resize-y min-h-[5rem]",
        "focus:bg-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30",
        invalid ? "border-rose-500/60 focus:border-rose-400" : "border-zinc-800 focus:border-indigo-500/50",
        className
      )}
      {...props}
    />
  );
});

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, className, children, ...props },
  ref
) {
  return (
    <select
      ref={ref}
      className={cn(
        "w-full rounded-lg border bg-zinc-900/50 px-3 text-sm text-zinc-100",
        "h-9 transition-colors appearance-none bg-no-repeat",
        "focus:bg-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30",
        invalid ? "border-rose-500/60 focus:border-rose-400" : "border-zinc-800 focus:border-indigo-500/50",
        className
      )}
      style={{
        backgroundImage: "url(\"data:image/svg+xml;charset=utf-8,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3 4.5L6 7.5L9 4.5' stroke='%2371717a' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
        backgroundPosition: "right 0.5rem center",
        paddingRight: "1.75rem",
      }}
      {...props}
    >
      {children}
    </select>
  );
});

export function Label({
  children,
  required,
  htmlFor,
  className,
}: {
  children: ReactNode;
  required?: boolean;
  htmlFor?: string;
  className?: string;
}) {
  return (
    <label htmlFor={htmlFor} className={cn("text-xs font-medium text-zinc-300 mb-1.5 block", className)}>
      {children}
      {required && <span className="ml-0.5 text-rose-400">*</span>}
    </label>
  );
}

export function FieldHint({ children, error }: { children?: ReactNode; error?: boolean }) {
  if (!children) return null;
  return <p className={cn("mt-1.5 text-xs", error ? "text-rose-400" : "text-zinc-500")}>{children}</p>;
}
